/**
 * TaskEditorDialog — Feb 2026 · Phase 84.28
 *
 * Full-featured pop-up editor for a daily task. Used by:
 *   • DailyTasksWidget (dashboard weekly Kanban)
 *   • Anywhere else a Kanban card needs full-edit access
 *
 * Fields:
 *   • Title (required)
 *   • Description (textarea)
 *   • Due date (native date picker; clearable)
 *   • Status (planned · in_progress · done · blocked)
 *   • Project (assignable to any daily_task_sync project)
 *   • Assignee (any active internal user — admin / client_manager / founder only)
 *   • Image (upload · replace · remove)
 *   • Delete task
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Image as ImageIcon, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import usePermissions from "@/lib/usePermissions";
import { DAILY_TASK_STATUSES, STATUS_LABEL } from "@/lib/dailyTaskStatus";

const NO_PROJECT_VALUE = "__none__";

export default function TaskEditorDialog({ open, task, onOpenChange, onSaved, onDeleted }) {
  const can = usePermissions();
  const canAssign = can("tasks_admin") || can("tasks_assign");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [status, setStatus] = useState("planned");
  const [assigneeId, setAssigneeId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [priorityId, setPriorityId] = useState("");  // Phase 84.33
  const [imageUrl, setImageUrl] = useState(null);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [priorities, setPriorities] = useState([]);  // Phase 84.33 — all open priorities the user can see
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Hydrate lookups whenever the dialog opens.
  useEffect(() => {
    if (!open || !task) return;
    setTitle(task.title || "");
    setDescription(task.description || "");
    setDueDate(task.due_date || "");
    setStatus(task.status || "planned");
    setAssigneeId(task.assignee_id || "");
    setProjectId(task.project_id || "");
    setPriorityId(task.priority_id || "");
    setImageUrl(task.image_url || null);
    // Lazy-load: only fetch these when the dialog is actually open.
    Promise.all([
      api.get("/daily-tasks/projects").then((r) => r.data || []).catch(() => []),
      canAssign ? api.get("/users?kind=internal").then((r) => r.data || []).catch(() => []) : Promise.resolve([]),
      // Phase 84.33 — fetch every open priority the user can attach this task to.
      api.get("/priorities/all-open").then((r) => r.data || []).catch(() => []),
    ]).then(([p, m, pr]) => {
      setProjects(p);
      setMembers(m.filter((u) => (u.status || "active") === "active"));
      setPriorities(pr);
    });
  }, [open, task, canAssign]);

  const uploadImage = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("Image too large (max 15MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(data.path);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  }, []);

  const save = useCallback(async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const patch = {
        title: title.trim(),
        description,
        due_date: dueDate || "",
        status,
        project_id: projectId || "",
        priority_id: priorityId || "",  // Phase 84.33
        image_url: imageUrl || "",
      };
      if (canAssign) patch.assignee_id = assigneeId || "";
      await api.patch(`/daily-tasks/${task.id}`, patch);
      toast.success("Saved");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }, [title, description, dueDate, status, projectId, priorityId, imageUrl, assigneeId, canAssign, task, onSaved, onOpenChange]);

  const remove = useCallback(async () => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await api.delete(`/daily-tasks/${task.id}`);
      toast.success("Deleted");
      onDeleted && onDeleted();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [task, onDeleted, onOpenChange]);

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="task-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-base">Edit task</DialogTitle>
          {(task.created_by_name || task.created_at) && (
            <div className="text-[10px] text-slate-500 mt-1" data-testid="task-editor-added-by">
              Added by <span className="font-semibold text-slate-700">{task.created_by_name || "—"}</span>
              {task.created_at && (
                <> · {new Date(task.created_at).toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
              )}
            </div>
          )}
          {/* Phase 84.33 — pill surfaced when this task mirrors a marketing draft.
              Edits here propagate back to the draft on the marketing calendar. */}
          {task.linked_draft_id && (
            <div className="inline-flex items-center gap-1.5 text-[10px] mt-1.5 px-2 py-0.5 rounded-sm bg-pink-50 text-pink-700 border border-pink-200 w-fit" data-testid="task-editor-linked-draft">
              <ImageIcon size={10} /> Linked to marketing post · edits sync back to the calendar
            </div>
          )}
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <Label className="label-overline">Title</Label>
            <Input className="rounded-sm mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="task-editor-title" />
          </div>
          <div>
            <Label className="label-overline">Description</Label>
            <Textarea rows={3} className="rounded-sm mt-1 resize-none" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Notes, links, sub-steps…" data-testid="task-editor-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-overline">Due date</Label>
              <Input type="date" className="rounded-sm mt-1" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="task-editor-due-date" />
            </div>
            <div>
              <Label className="label-overline">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="task-editor-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[...DAILY_TASK_STATUSES].map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-overline">Project</Label>
              <Select value={projectId || NO_PROJECT_VALUE} onValueChange={(v) => setProjectId(v === NO_PROJECT_VALUE ? "" : v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="task-editor-project"><SelectValue placeholder="No project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PROJECT_VALUE}>— No project —</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Assignee{!canAssign && " (locked)"}</Label>
              <Select value={assigneeId || NO_PROJECT_VALUE} onValueChange={(v) => setAssigneeId(v === NO_PROJECT_VALUE ? "" : v)} disabled={!canAssign}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="task-editor-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PROJECT_VALUE}>— Unassigned —</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Phase 84.33 — optional link to a weekly priority. Available on
              every task so any task can roll up under a priority. */}
          <div>
            <Label className="label-overline">Priority link <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Select value={priorityId || NO_PROJECT_VALUE} onValueChange={(v) => setPriorityId(v === NO_PROJECT_VALUE ? "" : v)}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="task-editor-priority"><SelectValue placeholder="— No priority link —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PROJECT_VALUE}>— No priority link —</SelectItem>
                {priorities.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.title}{p.project_name ? ` · ${p.project_name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {priorityId && (
              <p className="text-[10px] text-slate-500 mt-1">Marking this task done also completes the linked priority when it&apos;s the last one.</p>
            )}
          </div>
          <div>
            <Label className="label-overline">Image</Label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <input id="task-editor-file" type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="task-editor-file-input" />
              <label htmlFor="task-editor-file" className="text-[11px] inline-flex items-center gap-1.5 border border-slate-200 hover:border-slate-400 rounded-sm px-2 py-1 cursor-pointer bg-white">
                <ImageIcon size={12} /> {uploading ? "Uploading…" : imageUrl ? "Replace" : "Add image"}
              </label>
              {imageUrl && (
                <>
                  <img src={imageUrl} alt="" className="w-12 h-12 object-cover rounded-sm border border-slate-200" data-testid="task-editor-image-preview" />
                  <button type="button" onClick={() => setImageUrl(null)} className="text-[10px] text-red-600 hover:underline inline-flex items-center gap-0.5" data-testid="task-editor-image-remove">
                    <X size={10} /> Remove
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-3">
          <Button variant="outline" onClick={remove} className="rounded-sm text-red-700 border-red-200 hover:bg-red-50 mr-auto" data-testid="task-editor-delete">
            <Trash2 size={12} className="mr-1" /> Delete
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="task-editor-save">
            {saving && <Loader2 size={12} className="animate-spin mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
